import _ from 'lodash';

import PurchaseOrderForm from './purchase_order_form';
import LineItemForm from './line_item_form';

/**
 * Form that manages every other form on the page
 * - responsible for top-level form actions like save and submit
 */
export default class Form {
  constructor(poNumber, {$injector, foodhub, userCanEdit}) {
    this.loadCatalogProducts = this.loadCatalogProducts.bind(this);
    this.getCatalogProduct = this.getCatalogProduct.bind(this);
    this.poNumber = poNumber;
    this.$injector = $injector;
    this.foodhub = foodhub;
    this.userCanEdit = userCanEdit;
    if (this.poNumber == null) {
      throw new Error('poNumber required');
    }
    this.Promise = this.$injector.get('Promise');
    this.Catalog = this.$injector.get('Catalog');
    this.PurchaseOrderProduct = this.$injector.get('PurchaseOrderProduct');
    this.PurchasingUom = this.$injector.get('PurchasingUom');
    this.isLoading = false;
  }

  loadData() {
    const LineItem = this.$injector.get('LineItem');
    const PurchaseOrder = this.$injector.get('PurchaseOrder');
    const Supplier = this.$injector.get('Supplier');

    this.isLoading = true;
    return this.Promise.props({
      suppliers: Supplier.getSuppliers(),
      purchaseOrder: PurchaseOrder.query({
        poNumber: this.poNumber,
        foodhubSlug: this.foodhub.slug,
      }).$promise.then((purchaseOrders) => {
        if (purchaseOrders.length > 1) {
          throw new Error('Expected only 1 purchase order');
        }
        if (purchaseOrders.length === 0) {
          throw new Error(`Purchase order #${this.poNumber} not found`);
        }
        return purchaseOrders[0];
      }),
    })
      .then(({purchaseOrder}) => {
        this.purchaseOrder = purchaseOrder;
        return this.Promise.props({
          lineItems: LineItem.query({purchaseOrderId: this.purchaseOrder._id}).$promise,
          catalogProducts: this.loadCatalogProducts(this.purchaseOrder),
          supplier: Supplier.getById(this.purchaseOrder.vendorId),
        });
      })
      .then(({lineItems, supplier, catalogProducts}) => {
        this.supplier = supplier;
        const purchasingUomIds = _.compact(
          catalogProducts.map((catalogProduct) => catalogProduct.purchasingOptionId),
        );
        const uniquePurchasingUomIds = _.uniq(purchasingUomIds);
        return this.Promise.props({
          purchasingUoms: this.getPurchasingUomsByIds(uniquePurchasingUomIds),
          lineItems,
        });
      })
      .then(({lineItems}) => {
        this.purchaseOrderMetaData = {
          supplier: this.supplier,
          foodhub: this.foodhub,
          getPurchasingUom: this.getPurchasingUom.bind(this),
          getCatalogProduct: this.getCatalogProduct,
          loadCatalogProducts: this.loadCatalogProducts,
          getPurchaseOrderProductByLegacyId: this.getPurchaseOrderProductByLegacyId.bind(this),
          userCanEdit: this.userCanEdit,
          $injector: this.$injector,
        };
        this._mapPurchaseOrderDataToForm({
          purchaseOrder: this.purchaseOrder,
          lineItems,
        });

        this.isDataLoaded = true;
      })
      .finally(() => (this.isLoading = false));
  }

  // eg. if a new catalog product has been added
  loadCatalogProducts() {
    return this.Catalog.query({
      foodHub: this.foodhub.slug,
      'vendor._id': this.purchaseOrder.vendorId,
    }).$promise.then((catalogProducts) => {
      this.catalogProductsById = _.keyBy(catalogProducts, (catalogProduct) => catalogProduct._id);
      return catalogProducts;
    });
  }

  getCatalogProduct(id) {
    return this.catalogProductsById[id];
  }

  getPurchasingUom(id) {
    return this.purchasingUomsById[id];
  }

  getPurchaseOrderProductByLegacyId(legacyId) {
    return this.PurchaseOrderProduct.getPurchaseOrderProductByLegacyId(legacyId);
  }

  getPurchasingUomsByIds(ids) {
    return this.PurchasingUom.getPurchasingUomsByIds(ids).then((purchasingUoms) => {
      this.purchasingUomsById = _.keyBy(purchasingUoms, (purchasingUom) => purchasingUom.id);
      return purchasingUoms;
    });
  }

  canPerformAction(action) {
    if (!this.userCanEdit) {
      return false;
    }

    switch (action) {
      case 'addLineItemForm':
        return this.editMode;
      case 'save':
        return this.purchaseOrder.status === 'new';
      case 'submit':
        if (!this.isValid()) {
          return false;
          // if new, every change is valid
        } else if (this.purchaseOrder.status === 'new') {
          return true; // new purchase orders aren't being updated
          // Otherwise, check to make sure something has changed before resubmitting
        }
        return (
          _.some(this.lineItemForms, (lineItemForm) => lineItemForm.isEdited()) ||
          this.purchaseOrderForm.isEdited()
        );

      case 'edit':
        return !['new', 'closed', 'cancelled'].includes(this.purchaseOrder.status);
      case 'confirm':
        return (
          !['new', 'closed', 'cancelled'].includes(this.purchaseOrder.status) &&
          this._canConfirmAllLineItemForms()
        );
      case 'unconfirm':
        return (
          this.purchaseOrder.status === 'confirmed' && this.purchaseOrder.vendorArrivedAt == null
        );
      case 'cancel':
        return (
          !['new', 'closed', 'cancelled'].includes(this.purchaseOrder.status) &&
          this.purchaseOrder.vendorArrivedAt == null &&
          this._canCancelAllLineItemForms()
        );
      default:
        return true;
    }
  }

  reset() {
    return this._mapPurchaseOrderDataToForm({
      purchaseOrder: this.purchaseOrder,
      lineItems: this.lineItems,
    });
  }

  /**
   * Validate that all form values are valid,
   * assign them to the purchaseOrder / lineItems
   * and save.
   * @resolves {lineItems, purchaseOrder} - updated resources (not including removed lineItems)
   */
  save() {
    return this.Promise.try(() => {
      this.isLoading = true;
      const {dropoffDay, dropoffWindow} = this.purchaseOrderForm.getLineItemMetaData();
      return this.Promise.props({
        lineItems: this._saveLineItems({dropoffDay, dropoffWindow}),
        purchaseOrder: this.purchaseOrderForm.save({}),
      });
    })
      .tap(this._mapPurchaseOrderDataToForm.bind(this))
      .finally(() => (this.isLoading = false));
  }

  assertValid() {
    return this.Promise.try(() => {
      if (!this.isValid()) {
        this.showErrors = true;
        throw new Error('Please fix form errors highlighted in red.');
      }
      this.showErrors = false;
    });
  }

  /**
   * Save and submit PO and lineItems
   * @resolves {lineItems, purchaseOrder} - updated resources
   */
  saveAndSubmit(param) {
    if (param == null) {
      param = {};
    }
    const {autoConfirm} = param;
    return this.Promise.try(() => {
      this.isLoading = true;
      return this.save();
    })
      .then(() => this.purchaseOrder.$submit({autoConfirm}))
      .tap(this._mapPurchaseOrderDataToForm.bind(this))
      .finally(() => (this.isLoading = false));
  }

  confirm() {
    return this.Promise.try(() => (this.isLoading = true))
      .then(() => this.purchaseOrder.$confirm({}))
      .tap(this._mapPurchaseOrderDataToForm.bind(this))
      .finally(() => (this.isLoading = false));
  }

  reconfirm() {
    return this.Promise.try(() => {
      this.isLoading = true;
      return this.save();
    })
      .then(() => this.purchaseOrder.$reconfirm({}))
      .tap(this._mapPurchaseOrderDataToForm.bind(this))
      .finally(() => (this.isLoading = false));
  }

  unconfirm() {
    return this.Promise.try(() => {
      this.isLoading = true;
      return this.purchaseOrder.$unconfirm({});
    })
      .tap(this._mapPurchaseOrderDataToForm.bind(this))
      .then(() => (this.editMode = true))
      .finally(() => (this.isLoading = false));
  }

  clone({foodhubSlug}) {
    return this.Promise.try(() => {
      this.isLoading = true;
      return this.purchaseOrder.$clone({foodhubSlug});
    }).finally(() => (this.isLoading = false));
  }

  cancel() {
    return this.Promise.try(() => {
      this.isLoading = true;
      return this.purchaseOrder.$cancel({});
    })
      .tap(this._mapPurchaseOrderDataToForm.bind(this))
      .finally(() => (this.isLoading = false));
  }

  // Edit po/line items which have already been submitted
  edit() {
    this.editMode = true;
  }

  isValid() {
    return (
      this.purchaseOrderForm.isValid() &&
      this._areAllLineItemFormsValid() &&
      !this.lineItemForms.every(({status}) => status === 'cancelled')
    );
  }

  isEditable(lineItemForm, field) {
    return this.editMode && lineItemForm.isFieldEditable(field);
  }

  canReorderLineItems() {
    return this.purchaseOrder.status === 'new';
  }

  /**
   * Save all lineItems that have changed, destroy all that need to be deleted
   */
  _saveLineItems({dropoffDay, dropoffWindow}) {
    return this.Promise.map(this.lineItemFormsToDestroy, (lineItemForm) => lineItemForm.destroy())
      .then(() =>
        this.Promise.map(this.lineItemForms, (lineItemForm) =>
          lineItemForm.save({dropoffDay, dropoffWindow}),
        ),
      )
      .then((savedLineItems) => _.flatten(savedLineItems));
  }

  // other alternative is to not save lineItem number until we actually save, and
  // make the form responsible for managing lineItem order
  removeLineItemForm(lineItemForm) {
    if (lineItemForm.lineItem.status === 'new') {
      // decrement line item numbers above the line item being removed
      for (const liForm of Array.from(this.lineItemForms)) {
        if (liForm.number > lineItemForm.number) {
          liForm.number -= 1;
        }
      }
      const i = _.indexOf(this.lineItemForms, lineItemForm); // ng-repeat sorting will munge passed index...
      this.lineItemForms.splice(i, 1);
      // item has been saved, queue it up for removal
      return this.lineItemFormsToDestroy.push(lineItemForm);
    }
    return lineItemForm.cancel();
  }

  addLineItemForm() {
    const LineItem = this.$injector.get('LineItem');

    const lineItem = new LineItem({
      catalogId: null,
      purchaseOrderId: this.purchaseOrder._id,
      status: 'new',
      startSellingWhen: 'received',
      foodhubSlug: this.foodhub.slug,
      orderQuantity: 1,
      number: this.lineItemForms.length + 1,
    });
    const lineItemForm = new LineItemForm(lineItem, this.purchaseOrder, this.purchaseOrderMetaData);
    this.lineItemForms.push(lineItemForm);
  }

  _mapPurchaseOrderDataToForm({purchaseOrder, lineItems}) {
    this.isLoading = false;
    this.action = undefined;
    this.lineItems = lineItems;
    this.purchaseOrder = purchaseOrder;
    this.lineItemFormsToDestroy = [];
    this.lineItemForms = lineItems.map(
      (lineItem) => new LineItemForm(lineItem, purchaseOrder, this.purchaseOrderMetaData),
    );
    this.editMode = purchaseOrder.status === 'new';
    if (lineItems.length === 0 && purchaseOrder.status === 'new') {
      this.addLineItemForm();
    }
    this.purchaseOrderForm = new PurchaseOrderForm(purchaseOrder, this.purchaseOrderMetaData);
  }

  getCatalogProductOptions() {
    const newItem = {
      _id: 'newItem',
      name: 'New catalog item...',
    };

    const activeCatalogProducts = _(this.catalogProductsById)
      .values()
      .filter(({status}) => status === 'active')
      .value();

    return [...activeCatalogProducts, newItem];
  }

  /**
   * @return {Number} subtotal in cents of all active line items
   */
  getSubtotal() {
    return _.sumBy(this.getActiveLineItemForms(), (lineItemForm) => lineItemForm.getTotalPrice());
  }

  getActiveLineItemForms() {
    return this.lineItemForms.filter(
      (lineItemForm) => !['cancelled', 'edited'].includes(lineItemForm.status),
    ); // extract as enum?? what do you call this?
  }

  getJustCancelledLineItemForms() {
    return this.lineItemForms.filter(
      (lineItemForm) => lineItemForm.status === 'cancelled' && lineItemForm.isFieldEdited('status'),
    );
  }

  isEditingPurchaseOrderMetadata() {
    return this.editMode && this.purchaseOrder.status !== 'confirmed';
  }

  _areAllLineItemFormsValid() {
    return Boolean(
      this.lineItemForms.length > 0 &&
        _.every(this.lineItemForms, (lineItemForm) => lineItemForm.isValid()),
    );
  }

  _canCancelAllLineItemForms() {
    return (
      this._existsUnremovedLineItem() &&
      _.every(this.lineItemForms, (lineItemForm) => lineItemForm.status !== 'closed')
    );
  }

  _canConfirmAllLineItemForms() {
    return (
      this._existsUnremovedLineItem() &&
      _.every(this.lineItemForms, (lineItemForm) =>
        ['edited', 'cancelled', 'submitted'].includes(lineItemForm.status),
      )
    );
  }

  _existsUnremovedLineItem() {
    return (
      _.find(
        this.lineItemForms,
        (lineItemForm) => !['edited', 'cancelled'].includes(lineItemForm.status),
      ) != null
    );
  }
}
